import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { contactInfo } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { FadeIn, SlideIn } from '../components/PageTransition';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = (e) => {
  e.preventDefault();

  const phoneNumber = "+918484875750"; // your WhatsApp number with country code

  const message = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Subject: ${formData.subject}

Message:
${formData.message}
  `;

  const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  window.open(whatsappURL, "_blank");

  toast.success("Redirecting to WhatsApp...");
};
  const contactItems = [
    { icon: MapPin, label: 'Location', value: contactInfo.location },
    { icon: Phone, label: 'Phone', value: contactInfo.phone },
    { icon: Mail, label: 'Email', value: contactInfo.email },
    { icon: Clock, label: 'Hours', value: contactInfo.hours }
  ];



  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-gradient-to-r from-amber-900 to-amber-800 mt-[130px]">
        <motion.div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1582228096960-7f5d2ec4aa8e")',
            backgroundSize: 'cover',
          }}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center">
          <motion.h1
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Get in Touch
          </motion.h1>
          <motion.p
            className="text-xl text-amber-100 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We'd love to hear from you. Reach out for reservations, inquiries, or feedback
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <SlideIn direction="left">
                <h2 className="text-3xl font-bold text-amber-900 mb-8">
                  Contact Information
                </h2>
                <div className="space-y-6 mb-8">
                  {contactItems.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card className="border-amber-100 hover:shadow-lg transition-all duration-300">
                        <CardContent className="p-6 flex items-start space-x-4">
                          <motion.div
                            className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0"
                            whileHover={{ scale: 1.1, rotate: 360 }}
                            transition={{ duration: 0.5 }}
                          >
                            <item.icon className="w-6 h-6 text-amber-700" />
                          </motion.div>
                          <div>
                            <h4 className="font-semibold text-amber-900 mb-1">
                              {item.label}
                            </h4>
                            <p className="text-gray-600">{item.value}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                {/* Map Placeholder */}
                <motion.div
                  className="h-64 bg-amber-100 rounded-xl overflow-hidden shadow-lg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-200 to-amber-300">
                    <div className="text-center">
                      <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.2068545263073!2d73.93468667496295!3d18.51955218257379!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c319ba0052f3%3A0x9f14f316fc8c1e20!2sJust%20Bengali%20Restaurant%20Magarpatta!5e0!3m2!1sen!2sin!4v1778145279544!5m2!1sen!2sin" width="710" height="230"  allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
                      <p className="text-amber-900 font-medium">Hadpasr Pune Location</p>
                    </div>
                  </div>
                </motion.div>
             
              </SlideIn>
            </div>

            {/* Contact Form */}
            <SlideIn direction="right">
              <Card className="border-amber-100 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-amber-900 mb-6">
                    Send us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <Input
                        name="name"
                        placeholder="Your Name *"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-amber-200 focus:border-amber-500 py-6"
                      />
                    </motion.div>
                    <motion.div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Input
                        name="email"
                        type="email"
                        placeholder="Email *"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-amber-200 focus:border-amber-500 py-6"
                      />
                      <Input
                        name="phone"
                        type="tel"
                        placeholder="Phone *"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="border-amber-200 focus:border-amber-500 py-6"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Input
                        name="subject"
                        placeholder="Subject *"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="border-amber-200 focus:border-amber-500 py-6"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Textarea
                        name="message"
                        placeholder="Your Message *"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="border-amber-200 focus:border-amber-500"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Button
                        type="submit"
                        className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
                      >
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </Button>
                    </motion.div>
                  </form>
                </CardContent>
              </Card>
                 <motion.div
                  className="h-64 bg-amber-100 rounded-xl overflow-hidden shadow-lg mt-[86px]"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-200 to-amber-300">
                    <div className="text-center">
                    
                      <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3782.6517479257755!2d73.93315467496352!3d18.54463308255354!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c1a37aefd953%3A0x877d59368bf91f4e!2sJust%20Bengali%20Restaurant!5e0!3m2!1sen!2sin!4v1778144845878!5m2!1sen!2sin" width="705" height="230"  allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
                    <p className="text-amber-900 font-medium">Kharadi Pune Location</p>
                    </div>
                  </div>
                </motion.div>
            </SlideIn>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <FadeIn>
        <section className="py-16 bg-amber-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-white mb-4">
                Need Immediate Assistance?
              </h3>
              <p className="text-amber-100 mb-6 text-lg">
                Call us directly for urgent queries or same-day reservations
              </p>
              <motion.a
                href={`tel:${contactInfo.phone}`}
                className="inline-flex items-center gap-2 bg-white text-amber-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-amber-50 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Phone className="w-5 h-5" />
                {contactInfo.phone}
              </motion.a>
            </div>
          </div>
        </section>
      </FadeIn>
    </div>
  );
};

export default ContactPage;
